import {Component, OnDestroy} from '@angular/core';
import {CardComponent} from '../../../../../shared/components/card/card.component';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {CardContentDirective} from '../../../../../shared/components/card/card-content.directive';
import {ActivatedRoute, Router} from '@angular/router';
import {firstValueFrom, Subscription} from 'rxjs';
import {ObservationManagementService} from '../../../../../data/observations/observation-management.service';
import {ObservationGroupTypeDto} from '../../../../../shared/models/observation-group';
import {RowWithToggleComponent} from '../../row-with-toggle/row-with-toggle.component';
import {MatError, MatFormField, MatSuffix} from '@angular/material/input';
import {NgxColorsModule} from 'ngx-colors';
import {MatOption, MatSelect} from '@angular/material/select';
import {InstantErrorStateMatcherInstance} from '../../../../../shared/tools/InstantErrorStateMatcher';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {ObservationTypeDialogData, ObservationTypeDto} from '../../../../../shared/models/observation-type';
import {MatDivider, MatListItem, MatListItemMeta, MatListItemTitle, MatNavList} from '@angular/material/list';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {RootRoute, SettingsRoute} from '../../../../../shared/models/angular-routing';
import {MatIcon} from '@angular/material/icon';
import {debounceTime, filter} from 'rxjs/operators';
import {
  ConfirmLeaveUnsavedChangesDialogComponent
} from '../../confirm-leave-unsaved-changes-dialog/confirm-leave-unsaved-changes-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {ColorValidator} from '../../../../../shared/tools/color-validator';
import {
  UpdateObservationTypeDialogComponent
} from '../dialogs/update-observation-type-dialog/update-observation-type-dialog.component';
import {ToastService} from '../../../../../shared/services/toast.service';
import {
  CreateObservationTypeDialogComponent
} from '../dialogs/create-observation-type-dialog/create-observation-type-dialog.component';
import {
  ConfirmationDialogComponent
} from '../../../../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import {MatTooltip} from '@angular/material/tooltip';

function generateDurationValues(values: number[], hoursMultiplier: number, label: string) {
  return values.map(value => {
    return {
      duration: value * hoursMultiplier * 60 * 60,
      label: `${value} ${label}${value > 1 ? 's' : ''}`
    };
  });
}

@Component({
  selector: 'app-observation-detail',
  imports: [
    CardComponent,
    ReactiveFormsModule,
    RowWithToggleComponent,
    CardContentDirective,
    MatSuffix,
    NgxColorsModule,
    MatSelect,
    MatOption,
    MatFormField,
    MatProgressSpinner,
    MatNavList,
    MatListItemTitle,
    MatButton,
    MatListItem,
    MatDivider,
    MatMenu,
    MatMenuContent,
    MatMenuItem,
    MatIcon,
    MatIconButton,
    MatListItemMeta,
    MatMenuTrigger,
    MatError,
    MatTooltip,
  ],
  templateUrl: './observation-detail.component.html',
  styleUrls: ['../../../settings-fields.scss'],
})
export class ObservationDetailComponent implements OnDestroy {
  isLoading = false;
  isLoadingObservationTypes = false;
  isSaving = false;

  subscriptions: Subscription[] = [];
  mapColor: FormControl;
  isCartegraphEnabled = false;
  protected formGroup: FormGroup;
  protected readonly InstantErrorStateMatcherInstance = InstantErrorStateMatcherInstance;
  protected observationTypes: ObservationTypeDto[];
  protected readonly SettingsRoute = SettingsRoute;
  protected readonly RootRoute = RootRoute;
  protected durationValues =
    // hours
    [
      ...generateDurationValues([...Array(13).keys()].slice(1, 14), 1, 'hour'),
      ...generateDurationValues([...Array(7).keys()].slice(1, 8), 24, 'day'),
      ...generateDurationValues([...Array(4).keys()].slice(1, 4), 7 * 24, 'week'),
      {duration: 30 * 24 * 60 * 60, label: '1 month'}
    ];

  private group: ObservationGroupTypeDto;

  constructor(formBuilder: FormBuilder,
              route: ActivatedRoute,
              private readonly observationManagementService: ObservationManagementService,
              private readonly toastService: ToastService,
              private readonly dialog: MatDialog,
              private readonly router: Router) {
    this.setupForm(formBuilder);
    this.setupDataLoad(route);
  }

  handleAutoRemoveChange(value: boolean) {
    const {durationInSeconds} = this.formGroup.controls;
    if (value) {
      if (durationInSeconds.disabled) {
        durationInSeconds.setValue(this.durationValues[0].duration, {emitEvent: false});
        durationInSeconds.enable({emitEvent: false});
      }
    } else {
      durationInSeconds.disable({emitEvent: false});
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  navigateBack() {
    this.checkNavigateBack().then(result => {
      if (result) {
        this.router.navigate(['/', RootRoute.SETTINGS, SettingsRoute.MANAGE_OBSERVATIONS])
          .then();
      }
    });
  }

  async checkNavigateBack() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched({emitEvent: false});
      const dialogRef = this.dialog.open(ConfirmLeaveUnsavedChangesDialogComponent);
      return await firstValueFrom(dialogRef.afterClosed());
    }
    return Promise.resolve(true);
  }

  addObservationType() {
    const ref = this.dialog.open(CreateObservationTypeDialogComponent, {
      width: '500px'
    });

    firstValueFrom(ref.afterClosed()).then((result: ObservationTypeDialogData) => {
      if (result) {
        this.observationManagementService.createObservationType(this.group.id, {
          ...result
        } as ObservationTypeDto).then(() => {
          this.loadObservationTypes();
          this.toastService.long('Observation type has been added');
        });
      }
    });
  }

  updateObservationType(type: ObservationTypeDto) {
    const {title, abbreviation} = type;
    const ref = this.dialog.open(UpdateObservationTypeDialogComponent, {
      width: '500px',
      data: {
        title,
        abbreviation
      } as ObservationTypeDialogData
    });

    firstValueFrom(ref.afterClosed()).then((result: ObservationTypeDialogData) => {
      if (result) {
        this.observationManagementService
          .updateObservationType(this.group.id, type.id, {...type, ...result})
          .then(() => {
            this.loadObservationTypes();
            this.toastService.long('Observation type has been updated');
          });
      }
    });
  }

  removeObservationType(type: ObservationTypeDto) {
    const ref = this.dialog.open(ConfirmationDialogComponent, {
      width: '450px',
      data: {
        text: `Are you sure you want to delete observation type "${type.title} (${type.abbreviation})"?`
      },
    });

    firstValueFrom(ref.afterClosed()).then(result => {
      if (result) {
        this.observationManagementService.deleteObservationType(this.group.id, type.id).then(() => {
          this.loadObservationTypes();
          this.toastService.long('Observation type has been removed');
        });
      }
    });
  }

  private setupDataLoad(route: ActivatedRoute) {
    this.subscriptions.push(
      route.params.subscribe(params => {
        if (params.id) {
          this.isLoading = true;
          this.observationManagementService.getObservationTypeGroup(params.id).then(result => {
            this.group = result;

            this.loadObservationTypes();

            const {durationInSeconds, mapColor, isAudible} = result;
            this.formGroup.setValue({
              autoRemove: durationInSeconds !== 0,
              durationInSeconds: durationInSeconds || null,
              mapColor,
              isAudible,
            }, {emitEvent: false});
            this.handleAutoRemoveChange(durationInSeconds !== 0);
          }).finally(() => {
            this.isLoading = false;
          });
        }
      })
    );
  }

  private loadObservationTypes() {
    if (this.isLoadingObservationTypes) {
      return;
    }
    this.isLoadingObservationTypes = true;
    this.observationManagementService.getObservationTypesForTypeGroup(this.group.id)
      .then(types => {
        this.observationTypes = types;
      })
      .finally(() => {
        this.isLoadingObservationTypes = false;
      });
  }

  private setupForm(formBuilder: FormBuilder) {
    this.mapColor = formBuilder.control(null, {
      validators: [ColorValidator.instance]
    });
    this.formGroup = formBuilder.group({
      autoRemove: formBuilder.control(false),
      durationInSeconds: formBuilder.control(null, {validators: Validators.required}),
      mapColor: this.mapColor,
      isAudible: formBuilder.control(false),
    });

    this.subscriptions.push(
      this.formGroup.controls.autoRemove.valueChanges.subscribe(this.handleAutoRemoveChange.bind(this))
    );

    this.subscriptions.push(
      this.formGroup.valueChanges.pipe(
        debounceTime(500),
        filter(_ => this.formGroup.valid)
      ).subscribe(value => {
        this.isSaving = true;

        const {
          autoRemove,
          durationInSeconds,
          mapColor,
          isAudible
        } = value;
        this.observationManagementService.updateObservationTypeGroup(this.group.id, {
          name: this.group.name,
          durationInSeconds: autoRemove ? durationInSeconds : 0,
          mapColor,
          isAudible
        } as ObservationGroupTypeDto).finally(() => {
          this.isSaving = false;
        });
      })
    );
  }
}
